import {EventEmitter, Injectable} from '@angular/core';
import {Apollo} from "apollo-angular";
import {get_graph_token, get_sso_token} from "../../graphql/getEquityInstallments";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import {EquityInstallmentService} from "../../equity-installment.service";
import {BehaviorSubject, firstValueFrom, lastValueFrom} from "rxjs";
import {map, switchMap} from "rxjs/operators";
import { login } from "../../../auth/graphql/login"
import {insert_refresh_sso_token} from "../../graphql/updateEquityInstallments";
import {DatePipe} from "@angular/common";
import {CurrentUserService} from "../../../services/current-user.service";
import {InstallmentViewType} from "../equity-installment-home/equity-installment-home.component";

@Injectable({
  providedIn: 'root'
})
export class EquityInstallmentSharepointService {

  public defaultSharepointUrl = "https://graph.microsoft.com/v1.0/sites/"

  public defaultSubUrl = 'creallc.sharepoint.com:/sites/'

  public serviceLoaded:boolean = false

  private _ssoToken = null;
  private ssoToken = new BehaviorSubject(this._ssoToken)
  public ssoToken$ = this.ssoToken.asObservable()

  private _ssoRefreshToken = null;
  private ssoRefreshToken = new BehaviorSubject(this._ssoRefreshToken)
  public ssoRefreshToken$ = this.ssoRefreshToken.asObservable();

  private _siteId = null;
  private siteId = new BehaviorSubject(this._siteId)
  public siteId$ = this.siteId.asObservable();

  private _driveId = null;
  private driveId = new BehaviorSubject(this._driveId)
  public driveId$ = this.driveId.asObservable();

  private _userId:string = "";

  private _sharepointFiles = []
  private sharepointFiles = new BehaviorSubject(this._sharepointFiles)
  public sharepointFiles$ = this.sharepointFiles.asObservable();

  private _hasError = false;
  private hasError = new BehaviorSubject(this._hasError)
  public hasError$ = this.hasError.asObservable();

  private _triggerRefresh = false;
  private triggerRefresh = new BehaviorSubject(this._triggerRefresh)
  public triggerRefresh$ = this.triggerRefresh.asObservable()

  private _isLoading = true;
  private isLoading = new BehaviorSubject(this._isLoading)
  public isLoading$ = this.isLoading.asObservable();

  private _currentFolder:string;
  public fileSelected:EventEmitter<any> = new EventEmitter<any>()
  constructor(private apollo: Apollo, private http: HttpClient, private equityService: EquityInstallmentService, private datePipe: DatePipe, private userService: CurrentUserService) { }

  private async _getSsoToken(){
    const result = await firstValueFrom(this.apollo.query({
      query: get_sso_token,
      fetchPolicy: 'network-only'
    }));
    return result.data
  }

  private async _getToken(){
    const result = await firstValueFrom(this.apollo.query({
      query: get_graph_token
    }));
    return result.data
  }

  public async initializeSiteData(siteName:string, siteAndTokenOnly?: boolean){
    this._triggerRefresh = false;
    this.triggerRefresh.next(this._triggerRefresh)
    this._hasError = false;
    this.hasError.next(this._hasError)
    let url = this.defaultSharepointUrl + this.defaultSubUrl + siteName

    this.userService.user$.subscribe((response:any) => this._userId = response.id)
    this.isLoading.next(true)

    /// Get Site Id with graph token
    try {
      const siteInfo:any = await this.getSiteInfo(url)
      if(siteInfo.hasOwnProperty('status') && siteInfo.status==='error'){
        this._hasError = true;
        this.hasError.next(this._hasError)
        return;
      } else {
        this._siteId = siteInfo.id;
        this.siteId.next(this._siteId)
      }
    } catch (e:any) {
      this._hasError = true;
      this.hasError.next(this._hasError)
      return;
    }

    ///Get current sso token
    try {
      let result:any = await this._getSsoToken()
      let tokens = result.me.ssoTokens.filter((filter:any) => filter.active == true);
      tokens = [...tokens].sort((a:any, b:any) => new Date(b.last_verified).getTime() - new Date(a.last_verified).getTime())
      this._ssoToken = tokens[0].access_token
      this._ssoRefreshToken = tokens[0].refresh_token
      this.ssoToken.next(this._ssoToken)
      this.ssoRefreshToken.next(this._ssoRefreshToken)
    } catch (e:any) {
      this._hasError = true;
      this.hasError.next(this._hasError)
      return;
    }

    ///Get site drive data
    try {
      const siteDrive:any = await this.getSiteDrives()
      if((siteDrive.hasOwnProperty('status') && siteDrive.status==='error') || (siteDrive.hasOwnProperty('value') && siteDrive.value.length == 0)){
        this._hasError = true;
        this.hasError.next(this._hasError)
        if(siteDrive.code === 401 || siteDrive?.value?.length == 0){
          await this.refreshToken(siteName)
        }
        return;
      } else {
        let drive = siteDrive.value.filter((drive:any) => drive.name.toLowerCase() == 'documents')[0]
        this._driveId = drive.id;
        this.driveId.next(this._driveId)
      }
    } catch(e:any) {
      this._hasError = true;
      this.hasError.next(this._hasError)
      return;
    }

    ///Get Site files
    if(!siteAndTokenOnly){
      try {
        const siteFiles:any = await this.getSiteFiles()
        if(siteFiles.hasOwnProperty('status') && siteFiles.status==='error'){
          this._hasError = true;
          this.hasError.next(this._hasError)
          if(siteFiles.code === 401){
            await this.refreshToken(siteName)
          }
          return;
        } else {
          this._sharepointFiles = siteFiles.value
          this._currentFolder = siteFiles.value[0]?.parentReference?.name;
          this.sharepointFiles.next(this._sharepointFiles)
        }
      } catch (e:any) {
        this._hasError = true;
        this.hasError.next(this._hasError)
        return;
      }
    }

    this.isLoading.next(false)
    this.serviceLoaded = true;


  }

  public async getSiteInfo(url: string) {
    let graphToken:any = null;
    try {
      graphToken = await this._getToken()
    } catch (e){
      return {status: 'error'}
    }
    if(graphToken!==null){
      const headers = new HttpHeaders({
        "Authorization" : "Bearer " + graphToken.getToken
      })
      try {
        return await firstValueFrom(this.http.get(url, {headers}))
      } catch (e:any){
        return {status: 'error', code: e.status}
      }
    } else {
      return {status: 'error'}
    }

  }

  public async getSiteDrives(){
      const headers = new HttpHeaders({
        "Authorization" : "Bearer " + this._ssoToken
      })
      let url = this.defaultSharepointUrl + this._siteId + "/drives/"
      try {
        return await firstValueFrom(this.http.get(url, {headers: headers}))
      } catch(e:any) {
        return {status: 'error', code: e.status}
      }
  }

  public async getSiteFiles(){
    const headers = new HttpHeaders({
      "Authorization" : "Bearer " + this._ssoToken
    })
    let url = this.defaultSharepointUrl + this._siteId + "/drives/" + this._driveId + "/root:/General:/children"
    try {
      return await firstValueFrom(this.http.get(url, {headers: headers}))
    } catch(e:any){
      return {status: 'error', code: e.status}
    }
  }

  public async removeSiteFile(file:any){
    let headers = new HttpHeaders({
      "Authorization" : "Bearer " + this._ssoToken
    })
    let filePath = file.fileurl.split(this._currentFolder)[1]
    let url = this.defaultSharepointUrl + this._siteId + "/drives/" + this._driveId + "/root:/" + this._currentFolder + filePath + ":"
    this.http.delete(url, {headers:headers}).subscribe((result) => {
      if(result==null){
        let newFileArray = this._sharepointFiles.filter((spFile:any) => spFile.webUrl !== file.fileurl)
        this._sharepointFiles = newFileArray
        this.sharepointFiles.next(newFileArray)
      }
    })
  }

  public navigateToFolder(folder:string){
    let uriFolder = encodeURIComponent(folder)
    let url = this.defaultSharepointUrl + this._siteId + "/drives/" + this._driveId + "/root:/" + uriFolder + ":/children"
    this._currentFolder = folder;
    let headers = new HttpHeaders({
      "Authorization" : "Bearer " + this._ssoToken
    })
    this.http.get(url, {headers: headers}).subscribe((result:any) => {
      this._sharepointFiles = result.value
      this.sharepointFiles.next(this._sharepointFiles)
    })
  }

  public openFolderFromInstallment(installmentNum: number){
    let baseFolder = 'General/Funding/'
    if(this.equityService.viewType==InstallmentViewType.CERTIFICATED){
      baseFolder = baseFolder + 'Certificated/'
    }
    let folderUrl = baseFolder + "Installment-" + installmentNum
    this.navigateToFolder(folderUrl)
  }

  public openFolderFromFunding(installmentNum: number, fundingNum: number){
    let folderUrl = 'General/Funding/'
    if(this.equityService.viewType==InstallmentViewType.CERTIFICATED){
      folderUrl = folderUrl + 'Certificated/'
    }
    folderUrl = folderUrl + 'Installment-' + installmentNum + "/Funding-" + fundingNum
    this.navigateToFolder(folderUrl)
  }

  public uploadFileToDrive(file: File){
    let encodedURI = encodeURIComponent(`${this._currentFolder}/${file.name}`)
    let url = this.defaultSharepointUrl + this._siteId + `/drives/` + this._driveId + `/root:/` + encodedURI + `:/content`
    let headers = new HttpHeaders({
      "Authorization" : "Bearer " + this._ssoToken
    })
    return this.http.put(url, file, {headers})
  }

  public uploadLargeFileToDrive(file: File){
    let encodedUri = encodeURIComponent(`${this._currentFolder}/${file.name}`)
    /// Create upload session
    let createUploadSession = (file: File) => {
      const url = this.defaultSharepointUrl + this._siteId + `/drives/` + this._driveId + `/root:/` + encodedUri + `:/createUploadSession`

      const headers = new HttpHeaders({
        "Authorization" : "Bearer " + this._ssoToken
      })
      const body = {item: {
        '@microsoft.graph.conflictBehavior': 'rename',
          name: file.name
        }}
      return this.http.post(url, body, {headers})

    }

    let uploadFileIntoChunks = async (uploadUrl: string, file: File) => {
      const size = 5 * 1024 * 1024; //5MB
      const totalSize = file.size;
      let start = 0;

      while(start < totalSize) {
        const end = Math.min(start + size, totalSize)
        const chunk = file.slice(start, end)

        const headers = new HttpHeaders({
          'Content-Range': `bytes ${start}-${end - 1}/${totalSize}`
        });

        const buffer = await chunk.arrayBuffer()
        const body = new Uint8Array(buffer)

        let call = this.http.put(uploadUrl, body, {headers, observe: 'response'})
        let result = await lastValueFrom(call)
        start = end
      }
    }
    createUploadSession(file).subscribe((response:any) => {
      const uploadUrl = response.uploadUrl
      uploadFileIntoChunks(uploadUrl, file)
    })
  }


  public async refreshToken(siteName:string){
    let url = "https://login.microsoftonline.com/creallc.onmicrosoft.com/oauth2/v2.0/token"
    let clientId = "1c19998a-4749-4229-9583-4d40dc81a8db"
    let headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    })
    let body = new HttpParams()
      .set('client_id', clientId)
      .set('grant_type', 'refresh_token')
      .set('refresh_token', this._ssoRefreshToken ?? '')
      .set('scope', 'https://graph.microsoft.com/.default')

    try {

      let tokenResponse:any = await firstValueFrom(this.http.post(url,body, {headers}))
      this._ssoToken = tokenResponse.access_token
      this.ssoToken.next(this._ssoToken)
      this._ssoRefreshToken = tokenResponse.refresh_token
      this.ssoRefreshToken.next(this._ssoRefreshToken)
      let variables = {
        "refresh_token" : tokenResponse.refresh_token,
        "access_token" : tokenResponse.access_token,
        "user_id" : this._userId,
        "last_verified" : this.datePipe.transform(new Date(), 'yyyy-MM-dd HH:mm:ss'),
        "active" : true
      }
      this.apollo.mutate({mutation: insert_refresh_sso_token, variables: variables}).subscribe((result:any) => {
        this.initializeSiteData(siteName)
      })
      return;
    } catch(e:any) {
      if(e.status === 400){
        return {status: 'error', code: e.status}
      }
      return {status: 'error', code: e.status}
    }

  }

  public parseSharepointWebUrl(response:any){
    if(response.webUrl.includes('_layouts')){
      let referenceUrl = response.parentReference.path.split("root:")[1]
      let pathUrl = response.webUrl.split("_layouts")[0]
      return pathUrl + 'Shared Documents' + referenceUrl + "/" + response.name
    }
    return response.webUrl
  }



  public async checkValidFiles(path:string, siteName:string){
    if(this._siteId==null || this._driveId==null || this._ssoToken==null){
      await this.initializeSiteData(siteName, true)
    }

    const base = '/General/'
    const index = path.indexOf(base)

    if(index === -1){
      //handle invalid path
      return {status: 'error', code: "invalid sharepoint path", file: null}
    }

    const subPath = path.substring(index + base.length)
    let url = `https://graph.microsoft.com/v1.0/sites/${this._siteId}/drives/${this._driveId}/root:/General/${subPath}`
    let headers = new HttpHeaders({
      "Authorization" : "Bearer " + this._ssoToken
    })
    try {
      let httpResponse:any = await firstValueFrom(this.http.get(url, {headers}))
      return httpResponse
    } catch (e:any){
      if(e.status == 'error'){
        if(e.code === 404){
          return {status:'error', code: e.status, file: path}
        }
      }
    }
  }

  public onDrawerFileSelection(files:any[]){
    this.fileSelected.emit(files)
  }




}
